import Box from "@mui/material/Box";
import {FormContainerBox, ToggleDrawerButton} from "components/views/editor/styled";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import Drawer from "@mui/material/Drawer";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import TextField from "@mui/material/TextField";
import React, {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {LessonsTags} from "models/LessonsTags";
import {useTheme, styled} from "@mui/material/styles";
import {LessonFormValidationErrors} from "models/LessonFormValidationErrors";
import {LessonForm} from "components/views/editor/models/LessonForm";
import {convertImageFileToHtmlImage} from "helpers/fileHelper";
import {LessonFormFields} from "constants/lessonFormFields";
import {
  TitleTypography,
  SubtitleTypography,
  SpacedTypography,
  ImageUploadBox,
  ImagePreview,
  ImagePlaceholderBox,
  ErrorTypography,
  FieldTypography,
  StyledTextField,
  StyledAutocomplete,
  DescriptionTextField
} from "./LessonFormDrawer.styles";

const StyledHiddenInput = styled('input')({
  display: "none"
});

interface LessonFormDrawerProps {
    tags: LessonsTags;
    lessonFormData: LessonForm;
    formValidationErrors: LessonFormValidationErrors;
    onLessonDataChange: (lessonFormField: LessonFormFields, newLessonFormData: LessonForm) => void;
    isDefaultOpen?: boolean;
}

export const LessonFormDrawer = (props: LessonFormDrawerProps) => {
    const {tags, lessonFormData, formValidationErrors, onLessonDataChange} = props;

    const [drawerOpen, setDrawerOpen] = useState<boolean>(!!props.isDefaultOpen);
    const imageFileInputRef = useRef<HTMLInputElement>(null);
    const {t} = useTranslation();
    const theme = useTheme();
    const acceptedImageMimeTypes = "image/png, image/jpeg , image/jpg";

    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen)
    }

    const handleTrainingImageClick = () => {
        if (imageFileInputRef.current) imageFileInputRef.current.click();
    };

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || !event.target.files[0]) return;

        const file = event.target.files[0];

        if (!acceptedImageMimeTypes.includes(file.type)) return;

        const htmlImage = await convertImageFileToHtmlImage(file);
        onLessonDataChange(LessonFormFields.IMAGE, {
            ...lessonFormData,
            image: {file: file, width: htmlImage.naturalWidth, height: htmlImage.naturalHeight}
        });
    }

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onLessonDataChange(LessonFormFields.NAME, {...lessonFormData, name: event.target.value});
    }

    const handleTagAChange = (event: React.SyntheticEvent, newTagA: string | null) => {
        onLessonDataChange(LessonFormFields.TAG_A, {...lessonFormData, tagA: newTagA});
    }

    const handleTagBChange = (event: React.SyntheticEvent, newTagB: string | null) => {
        onLessonDataChange(LessonFormFields.TAG_B, {...lessonFormData, tagB: newTagB});
    }

    const handleMaxPointsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value.match(/[^0-9]/)) {
            event.target.value = event.target.value.replace(/[^0-9]/g, '');
        }
        const inputValue = parseInt(event.target.value);
        onLessonDataChange(LessonFormFields.MAX_POINTS, {...lessonFormData, maxPoints: isNaN(inputValue) ? 0 : inputValue});
    }

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onLessonDataChange(LessonFormFields.DESCRIPTION, {...lessonFormData, description: event.target.value});
    }

    return (
        <Box>
            <ToggleDrawerButton
                drawerOpen={drawerOpen}
                variant="outlined"
                startIcon={drawerOpen ? <WestIcon/> : <EastIcon/>}
                onClick={() => toggleDrawer()}>
                {drawerOpen ? t("lessonEditor.hideDetails") : t("lessonEditor.showDetails")}
            </ToggleDrawerButton>
            <Drawer
                disableEnforceFocus
                PaperProps={{style: {width: "28%", borderTopLeftRadius: 0, borderBottomLeftRadius: 0}}}
                slotProps={{backdrop: {sx: {backgroundColor: "transparent", backdropFilter: "blur(2px)"}}}}
                anchor={"left"}
                open={drawerOpen}
                onClose={() => toggleDrawer()}
            >
                <FormContainerBox>
                    <TitleTypography>{t("lessonEditor.createTraining")}</TitleTypography>
                    <SubtitleTypography theme={theme}>{t("lessonEditor.addDetails")}</SubtitleTypography>
                    <SpacedTypography>{t("lessonEditor.addImage")}</SpacedTypography>
                    <ImageUploadBox theme={theme} onClick={handleTrainingImageClick}>
                        <StyledHiddenInput
                            type="file"
                            ref={imageFileInputRef}
                            accept={acceptedImageMimeTypes}
                            onClick={(event) => (event.target as HTMLInputElement).value = ''}
                            onChange={handleImageChange}
                        />
                        {lessonFormData.image
                            ? <ImagePreview
                                // TODO: will this be called every re-render? (src=URL.createObjectURL)
                                src={URL.createObjectURL(lessonFormData.image.file)}
                                alt={"Lesson Avatar"}
                            />
                            : <ImagePlaceholderBox>
                                <InsertPhotoOutlinedIcon fontSize="large"/>
                            </ImagePlaceholderBox>
                        }
                    </ImageUploadBox>
                    <ErrorTypography color="error">{formValidationErrors[LessonFormFields.IMAGE]}</ErrorTypography>
                    <FieldTypography>{t("lessonEditor.addName")}</FieldTypography>
                    <StyledTextField
                        InputLabelProps={{shrink: true}}
                        InputProps={{style: {height: 38}}}
                        id="training-name"
                        label={t("lessonEditor.name")}
                        defaultValue={lessonFormData.name}
                        onChange={handleNameChange}
                        error={!!formValidationErrors[LessonFormFields.NAME]}
                        helperText={formValidationErrors[LessonFormFields.NAME]}
                        autoComplete="off"
                    />
                    <FieldTypography>{t("lessonEditor.addTags")}</FieldTypography>
                    <StyledAutocomplete
                        id="tag-a"
                        freeSolo
                        options={tags.tagsA}
                        value={lessonFormData.tagA || undefined}
                        onChange={handleTagAChange}
                        onInputChange={handleTagAChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                label={t("lessonEditor.tagA")}
                                InputLabelProps={{shrink: true}}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: {height: '38px'}
                                }}
                            />
                        )}
                    />
                    <StyledAutocomplete
                        id="tag-b"
                        freeSolo
                        options={tags.tagsB}
                        value={lessonFormData.tagB || undefined}
                        onChange={handleTagBChange}
                        onInputChange={handleTagBChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                label={t("lessonEditor.tagB")}
                                InputLabelProps={{shrink: true}}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: {height: '38px'}
                                }}
                            />
                        )}
                    />
                    <FieldTypography>{t("lessonEditor.addScore")}</FieldTypography>
                    <StyledTextField
                        InputLabelProps={{shrink: true}}
                        InputProps={{style: {height: 38}}}
                        id="max-score"
                        label={t("lessonEditor.maxScore")}
                        onChange={handleMaxPointsChange}
                        defaultValue={lessonFormData.maxPoints}
                        error={!!formValidationErrors[LessonFormFields.MAX_POINTS]}
                        helperText={formValidationErrors[LessonFormFields.MAX_POINTS]}
                        autoComplete="off"
                    />
                    <FieldTypography>{t("lessonEditor.addTrainingDescription")}</FieldTypography>
                    <DescriptionTextField
                        id="description"
                        label={t("lessonEditor.addDescription")}
                        InputLabelProps={{shrink: false, style: {display: "none"}}}
                        multiline
                        rows={6}
                        onChange={handleDescriptionChange}
                        defaultValue={lessonFormData.description}
                        autoComplete="off"
                    />
                </FormContainerBox>
            </Drawer>
        </Box>
    );
}